import { authService } from '@/lib/auth-service';
import { integrationDataService } from '@/lib/integration-data-service';
import { IntegrationTokenManager } from '@/lib/integration-token-manager';

import { sessionValidator } from '@/lib/session-validator';
import { supabase, type UserProfile } from '@/lib/supabase';
import { persistor, store } from '@/store';
import { clearSession, setLoading, setUser } from '@/store/slices/authSlice';
import { clearAllData } from '@/store/slices/dataSlice';
import { clearIntegrationData } from '@/store/slices/integrationSlice';
import { resetRecording } from '@/store/slices/recordingSlice';
import type { AuthError, User as SupabaseUser } from '@supabase/supabase-js';

type User = UserProfile;

class AuthReduxService {
  private otpRequests: Record<string, { count: number; lastRequest: number }> =
    {};
  private isSupabaseConfigured: boolean = false;

  constructor() {
    this.initializeSupabaseConfig();
    if (this.isSupabaseConfigured) {
      this.initializeAuthListener();
    }
  }

  private initializeSupabaseConfig() {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const supabaseSchema = import.meta.env.VITE_SUPABASE_SCHEMA;

    this.isSupabaseConfigured = !!(
      supabaseUrl &&
      supabaseAnonKey &&
      supabaseSchema &&
      supabaseUrl !== 'your_supabase_project_url' &&
      supabaseAnonKey !== 'your_supabase_anon_key' &&
      supabaseSchema !== 'your_supabase_schema' &&
      supabaseUrl.includes('supabase.co')
    );

    if (!this.isSupabaseConfigured) {
      // Fallback: clear session if not configured
      store.dispatch(clearSession());
    }
  }

  private initializeAuthListener() {
    if (!this.isSupabaseConfigured) return;

    // Get initial session
    this.getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        // Clean up realtime subscriptions (handled by component unmounting now)
        store.dispatch(clearSession());
      } else if (session?.user) {
        // Fetch and store user profile data
        await this.fetchUserProfile(session.user);
      }
    });

    // Cleanup subscription on unmount (handled by the service instance)
    return () => subscription.unsubscribe();
  }

  private async getInitialSession() {
    store.dispatch(setLoading(true));
    try {
      // Use enhanced session validation instead of basic session check
      const validation = await sessionValidator.validateSession();

      if (validation.isValid && validation.user) {
        console.log('✅ Session validation successful:', {
          hasSupabaseSession: validation.details.hasSupabaseSession,
          isTokenValid: validation.details.isTokenValid,
          canAccessDatabase: validation.details.canAccessDatabase,
          userProfileValid: validation.details.userProfileValid,
          userId: validation.user.id,
          userPhone: validation.user.phone,
        });

        // Set user data in Redux
        store.dispatch(setUser(validation.user));

        // Fetch integration partner data if user has an integration partner
        if (validation.user.ev_integration_partner_id) {
          try {
            const result =
              await integrationDataService.fetchIntegrationDataIfNeeded(
                validation.user.ev_integration_partner_id
              );

            if (result.success) {
              console.log(
                '✅ Integration data available for session restoration'
              );
            } else {
              console.warn(
                '⚠️ Failed to get integration data for session restoration:',
                result.error
              );
            }
          } catch (integrationError) {
            console.warn(
              '⚠️ Error getting integration data for session restoration:',
              integrationError
            );
            // Don't fail the session restoration if integration data fetch fails
          }
        }
      } else {
        console.warn('❌ Session validation failed:', {
          error: validation.error,
          details: validation.details,
        });

        // Clear invalid session from Redux (this handles the case where Redux thinks
        // user is authenticated but Supabase session is actually invalid)
        store.dispatch(clearSession());
      }
    } catch (error) {
      console.error('❌ Error during session validation:', error);
      // Clear session on any error
      store.dispatch(clearSession());
    } finally {
      store.dispatch(setLoading(false));
    }
  }

  private async fetchUserProfile(supabaseUser: SupabaseUser) {
    try {
      const { data: userData, error } = await supabase
        .from('ev_user_profiles')
        .select('*')
        .eq('auth_user_id', supabaseUser.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('❌ Error fetching user profile:', error);
        store.dispatch(clearSession());
        return;
      }

      if (!userData) {
        console.error('❌ User profile not found');
        store.dispatch(clearSession());
        return;
      }

      const user: User = userData;
      store.dispatch(setUser(user));

      // Fetch integration partner data if user has an integration partner
      if (user.ev_integration_partner_id) {
        try {
          const result =
            await integrationDataService.fetchIntegrationDataIfNeeded(
              user.ev_integration_partner_id
            );

          if (result.success) {
            // console.log('✅ Integration data available for session restoration');
            // console.log('📋 Partner:', result.partner?.name);
            // console.log('📋 Endpoints:', result.endpoints?.length);
          } else {
            // console.warn(
            //   '⚠️ Failed to get integration data for session restoration:',
            //   result.error
            // );
          }
        } catch (integrationError) {
          // console.warn(
          //   '⚠️ Error getting integration data for session restoration:',
          //   integrationError
          // );
          // Don't fail the session restoration if integration data fetch fails
        }
      } else {
        // console.log('ℹ️ User is not associated with any integration partner');
      }
    } catch (error) {
      console.error('❌ Error in fetchUserProfile:', error);
      store.dispatch(clearSession());
    }
  }

  private checkRateLimit(phone: string): boolean {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    const request = this.otpRequests[phone];

    if (!request) return true;

    if (now - request.lastRequest > fiveMinutes) {
      // Reset if more than 5 minutes have passed
      this.otpRequests[phone] = { count: 1, lastRequest: now };
      return true;
    }

    if (request.count >= 3) {
      return false; // Rate limited
    }

    this.otpRequests[phone] = { count: request.count + 1, lastRequest: now };
    return true;
  }

  // Public methods
  async signInWithPhone(phone: string): Promise<{ error: AuthError | null }> {
    // Check rate limiting
    if (!this.checkRateLimit(phone)) {
      return {
        error: new Error(
          'Too many OTP requests. Please wait 5 minutes before trying again.'
        ) as AuthError,
      };
    }

    try {
      // Use auth service to send OTP
      const result = await authService.sendOtp(phone);
      if (result.success) {
        return { error: null };
      } else {
        return {
          error: new Error(result.error || 'Failed to send OTP') as AuthError,
        };
      }
    } catch (error) {
      return { error: error as AuthError };
    }
  }

  async verifyOtp(
    phone: string,
    otp: string
  ): Promise<{ error: AuthError | null; user: User | null }> {
    try {
      // Use auth service for OTP verification
      const result = await authService.verifyOtp(phone, otp);

      if (result.success && result.user) {
        store.dispatch(setUser(result.user));

        // Fetch integration partner data if user has an integration partner
        if (result.user.ev_integration_partner_id) {
          try {
            const integrationResult =
              await integrationDataService.fetchIntegrationDataIfNeeded(
                result.user.ev_integration_partner_id
              );

            if (integrationResult.success) {
              // console.log('✅ Integration data fetched successfully');
              // console.log('📋 Partner:', integrationResult.partner?.name);
              // console.log('📋 Endpoints:', integrationResult.endpoints?.length);
            } else {
              // console.warn(
              //   '⚠️ Failed to fetch integration partner data:',
              //   integrationResult.error
              // );
            }
          } catch (integrationError) {
            // console.warn(
            //   '⚠️ Error fetching integration partner data:',
            //   integrationError
            // );
            // Don't fail the login if integration data fetch fails
          }
        } else {
          // console.log('ℹ️ User is not associated with any integration partner');
        }

        return { error: null, user: result.user };
      } else {
        return {
          error: new Error(
            result.error || 'OTP verification failed'
          ) as AuthError,
          user: null,
        };
      }
    } catch (error) {
      return { error: error as AuthError, user: null };
    }
  }

  async logout(): Promise<void> {
    try {
      // Clean up realtime subscriptions (handled by component unmounting now)

      // Sign out from Supabase
      if (this.isSupabaseConfigured) {
        await supabase.auth.signOut();
      }

      // Clear integration token from memory and storage
      IntegrationTokenManager.clearToken();

      // Clear all Redux store data
      store.dispatch(clearSession());
      store.dispatch(clearAllData());
      store.dispatch(clearIntegrationData());
      store.dispatch(resetRecording());
      persistor.purge(); // Clear persisted data

      // Clear any cached data or local storage
      localStorage.removeItem('persist:auth');
      localStorage.removeItem('persist:integration');
      localStorage.removeItem('persist:data');
      localStorage.removeItem('eyevantage_auth');
      localStorage.removeItem('eyevantage_integration');
      localStorage.removeItem('eyevantage_data');
      localStorage.removeItem('eyevantage_recording');
    } catch (error) {
      console.error('❌ Error during logout:', error);
      // Even if there's an error, still clear everything
      store.dispatch(clearSession());
      store.dispatch(clearAllData());
      store.dispatch(clearIntegrationData());
      store.dispatch(resetRecording());
      persistor.purge(); // Clear persisted data
    }
  }

  get isConfigured(): boolean {
    return this.isSupabaseConfigured;
  }
}

// Export singleton instance
export const authReduxService = new AuthReduxService();
